import React, { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { DatePickerPanel, AppliedSelection } from '../reactDatePickerV3/DatePickerPanel';
import { pickerStateFromFilter, filterPatchFromApplied, triggerLabel } from '../reactDatePickerV3/filterAdapter';
import { formatRangeReadout } from '../reactDatePickerV3/format';
import { PickerState } from '../../model/datePicker/pickerState';
import { LocalDate, ZonedTime } from '../../model/datePicker/types';
import { Filter } from '../../model/Filter';
import './datePickerField.scss';

interface Session {
  today: LocalDate,
  now: ZonedTime,
  initialState: PickerState
}

// Disparador + popover sobre DatePickerPanel. El panel en sí no sabe nada de
// abrir/cerrar ni del modelo Filter — eso es responsabilidad de este
// componente, que hace de costura con el resto de Header.
//
// El timezone del tenant sigue sin resolverse (confirmado al arrancar
// CX-638, no bloquea): today/now salen del reloj del navegador. Revisar
// cuando el backend exponga el timezone real.
const DatePickerField = ({ filter, onChange }: Props) => {
  const [ session, setSession ] = useState<Session | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if(!session) {
      return;
    }
    const onClickOutside = (event: MouseEvent) => {
      if(containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSession(null);
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [ session ]);

  const open = () => {
    const now = DateTime.now();
    const today = now.toFormat('yyyy-MM-dd') as LocalDate;
    setSession({
      today,
      now: { hour: now.hour, minute: now.minute },
      // Reabre con la última selección aplicada (CX-649). Los defectos (2
      // semanas vs periodo anterior) ya los siembra App en el filtro cuando no
      // hay fechas en la URL, así que la primera apertura los muestra igual.
      initialState: pickerStateFromFilter(filter, today)
    });
  };

  const handleApply = (selection: AppliedSelection) => {
    onChange(filterPatchFromApplied(selection));
    setSession(null);
  };

  const handleCancel = () => {
    setSession(null);
  };

  return (
    <div className="rdp-field" ref={containerRef}>
      <button type="button" className="form-select rdp-field__trigger" onClick={open}>
        {triggerLabel(filter, formatRangeReadout)}
      </button>
      {
        session &&
          <div className="rdp-field__popover">
            <DatePickerPanel
              initialState={session.initialState}
              today={session.today}
              now={session.now}
              onApply={handleApply}
              onCancel={handleCancel}
            />
          </div>
      }
    </div>
  );
}

interface Props {
  filter: Filter,
  onChange: (patch: Partial<Filter>) => void
}

export { DatePickerField };
